import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Stack, useTheme, alpha } from '@mui/material'
import { GridApiPremium } from '@mui/x-data-grid-premium'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { useEntitiesFromSearch } from 'api/search'
import { initialDealState } from 'components/Deals/grid/initialDealState'
import dayjs from 'dayjs'
import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Contact, Deal, NewDeal, Organization, OrganizationTableItem, Product } from 'types'
import { useSaveDealAndNavigate } from '../../api/aws/deals/useSaveDealAndNavigate'
import { useContacts } from '../../api/aws/useContacts'
import { useUpdateItems } from '../../api/aws/useUpdateItems'
import { useContactsById } from '../../api/customHooks/useContactsById'
import useTotals from '../../api/customHooks/useTotals'
import { useCreateDocument } from '../../api/PandaDoc/useCreateDocument'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { BoxScroller, CreateProjectTextField } from '../../styles/elements/styleElements'
import { dialogStyles as formDialogStyles } from '../../styles/utilities'
import { normalizeDate } from '../../utils/dateUtils'
import ContactsAutocomplete from '../Contacts/ContactsAutocomplete'
import DealTypeSelect from '../Deals/DealTypeSelect'
import EditVerticalAutocomplete from '../EditModeComponents/EditVerticalAutocomplete'
import FormLoading from '../Loading/FormLoading'
import DealsAutocomplete from '../TeamTasks/DealsAutocomplete'
import CreateDealDisclaimer from './CreateDealDisclaimer'
import CreateDealHeader from './CreateDealHeader'
import CreateDealOrgAutocomplete from './CreateDealOrgAutocomplete'
import ExistingDeal from './ExistingDealSelect'
import { getUniqueIdentifier } from './getUniqueIdentifier'
import SelectAgreementOption from './SelectAgreementOption'
import SelectInvoiceOption from './SelectInvoiceOption'
import StageDefaultTextInput from './StageDefaultTextInput'
import { dialogContentStyles } from './styles'
import { usePrefillDealForm } from './usePrefillDealForm'
import { ROLES } from 'config/roles.config'
import { useSyncSelectedContacts } from './useSyncSelectedContacts'
import ValidateDiscovery from './ValidateDiscovery'
import ValidateDiscoveryIcons from './ValidateDiscoveryIcons'

const verticalsRequiringApproval = [
  'Construction',
  'Tourism',
  'Marketing Agency',
  'Manufacturing',
  'Entertainment',
  'Restaurant (Food & Beverage)',
  'City/Government',
  'Financial Institution',
  'Technology',
  'Medical',
  'Lawyer',
  'Education',
  'Senior',
  'Homebuilding',
  'Build to rent',
]
interface PrefillTypes extends NewDeal {
  selectedOrganization: Organization
  // selectedDeal: NewDeal
}

interface CloneDealProps {
  open: boolean
  // onClose: (rowSelectionModel: GridRowSelectionModel) => void
  onClose: () => void
  prefillData: PrefillTypes
  cloneMode: string
  apiRef: RefObject<GridApiPremium>
}
const CloneDeal: FC<CloneDealProps> = ({ open, onClose, prefillData, cloneMode, apiRef }) => {
  const navigate = useNavigate()
  const { activeUser, isActiveUserLoading, isActiveUserError } = useActiveUser()
  const [selectedDeal, setSelectedDeal] = useState(prefillData ?? null)
  const { organizations, deals: deals, products } = useEntitiesFromSearch()
  const origin = useMemo(() => {
    if (!prefillData || !prefillData?.organizations?.[0]) return null
    const originDeal = deals?.find(d => d?.SK === prefillData?.SK)
    const originOrganization = organizations?.find(o => o?.org_uuid === prefillData?.organizations?.[0])
    return { originDeal, originOrganization }
  }, [])
  const { originDeal, originOrganization } = origin ?? {}
  const [newDeal, setNewDeal] = useState<OrganizationTableItem>(originDeal ?? null)
  const productRows = products?.filter(p => p?.deal_uuid === prefillData?.deal_uuid)
  const relatedProducts = useMemo(() => {
    if (selectedDeal) return products?.filter((product: Product) => product?.deal_uuid === selectedDeal?.deal_uuid)
  }, [selectedDeal])
  const organizationDeals = deals?.filter(d => d?.PK === prefillData?.selectedOrganization?.PK)
  const [selectedOrganization, setSelectedOrganization] = useState(prefillData?.selectedOrganization ?? null)
  const [selectedContacts, setSelectedContacts] = useState([])
  const { contacts, isContactsLoading, isContactsError } = useContacts(true, ['createDeal', 'contacts'])
  const { mutate: updateItems } = useUpdateItems()
  const [contactIds, setContactIds] = useState([])
  const {
    newContacts: fetchedContacts,
    isContactByIdLoading: fetchedContactsLoading,
    isContactsByIdError: fetchedContactsError,
  } = useContactsById(contactIds || [], 'cloneDeal')
  // const [localStage, setLocalStage] = useState<string>(newDeal?.stage)
  const [localStage, setLocalStage] = useState<string>('Proposal')
  const [openTooltip, setOpenTooltip] = useState(false) // To control tooltip visibility
  const [isCreating, setIsCreating] = useState(false)
  const [isError, setIsError] = useState(false)
  const theme = useTheme()
  const { mutate: createDocument } = useCreateDocument()
  const { showSnackbar } = useSnackbarContext()
  const [uniqueIdentifier, setUniqueIdentifier] = useState('')
  const totals = useTotals(productRows, newDeal?.implementationFee || 0, newDeal.type)
  const [validationErrors, setValidationErrors] = useState({
    organization: false,
    type: false,
    startDate: false,
    endDate: false,
    selectedDeal: false,
  })
  const [isValid, setIsValid] = useState(false)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [iconColor, setIconColor] = useState<string>('gray')
  const [noSignature, setNoSignature] = useState(false)
  const [isTest, setIsTest] = useState(false)
  const [dealTypeName, setDealTypeName] = useState('')
  const testUsers = [ROLES.SYSTEM_ADMIN]
  const [agreementOption, setAgreementOption] = useState(
    cloneMode === 'clone'
      ? 'New/Add-on Business'
      : cloneMode === 'replace'
      ? 'Replace Existing Business'
      : prefillData?.agreementOption
  )
  const [invoiceOption, setInvoiceOption] = useState(
    cloneMode === 'clone'
      ? 'New Invoice'
      : cloneMode === 'replace'
      ? 'Clone & Replace Existing Invoice'
      : prefillData?.invoiceOption
  )
  const { handleSaveDealAndNavigate } = useSaveDealAndNavigate()

  const closeCloneDeal = (event: any, reason: any) => {
    const currentRowSelectionModel = apiRef?.current?.state?.rowSelection
    if (Array.from(currentRowSelectionModel?.ids)?.length > 0) {
      Array.from(currentRowSelectionModel?.ids).forEach(id => {
        apiRef.current.selectRow(id, false)
      })
    }
    if (reason && reason === 'backdropClick') return

    resetDealState()
    onClose()
  }
  useSyncSelectedContacts({
    fetchedContacts,
    setSelectedContacts,
    setNewDeal,
  })
  const fetchContactsForOrganization = (organization: any) => {
    // Ensure primaryContactId is an array
    const primaryContactId =
      organization && Array.isArray(organization.primaryContact)
        ? organization.primaryContact
        : organization.primaryContact
        ? [organization.primaryContact]
        : []

    const contactIds = [...primaryContactId, ...(organization.contacts || [])]
    setContactIds(contactIds)
  }
  const handleSelectOrganization = (organization: any) => {
    setSelectedOrganization(organization)
    if (organization) {
      fetchContactsForOrganization(organization)
      setNewDeal(prev => ({
        ...prev,
        organizations: [organization.id || organization.org_uuid],
      }))

      // Check if the organization has a valid vertical
      if (!organization.vertical || organization.vertical.includes('No Vertical Assigned')) {
        console.log('No Valid Vertical Assigned')
        setIsValid(false) // Disable buttons
        setValidationErrors(prev => ({
          ...prev,
          organization: true, // Trigger error
          organizationHelperText: 'No valid vertical assigned to the organization', // Set helper text
        }))
      } else {
        setIsValid(true) // Enable buttons
        setValidationErrors(prev => ({
          ...prev,
          organization: false,
          organizationHelperText: '', // Clear the helper text when valid
        }))
      }
    } else {
      setSelectedContacts([])
      setSelectedDeal(null)
      setNewDeal(prev => ({
        ...prev,
        organizations: [],
        contacts: [],
      }))
      setIsValid(false) // Disable buttons if no organization is selected
    }
  }
  usePrefillDealForm({
    prefillData,
    organizations,
    contacts,
    handleSelectOrganization,
    setSelectedContacts,
    setNewDeal,
    setContactIds,
    cloneMode,
  })
  const allContacts = useMemo(() => {
    const contactMap = new Map()
    contacts?.forEach(contact => contactMap.set(contact.contacts_uuid, contact))
    fetchedContacts?.forEach(contact => contactMap.set(contact.contacts_uuid, contact))
    return Array.from(contactMap.values())
  }, [contacts, fetchedContacts])

  const resetDealState = () => {
    setNewDeal((initialDealState as unknown) as OrganizationTableItem)
    setSelectedOrganization(null)
    setSelectedContacts([])
    setUniqueIdentifier('')
    setIsError(false)
    setIconColor('gray')
    setNoSignature(false)
    setDealTypeName('')
  }
  useEffect(() => {
    if (selectedOrganization && newDeal.type !== 'IO') {
      if (
        selectedOrganization?.vertical &&
        Array.isArray(selectedOrganization?.vertical) &&
        selectedOrganization?.vertical.some(v => verticalsRequiringApproval.includes(v))
      ) {
        setNewDeal(prev => ({
          ...prev,
          isApproved: false,
          needsApproval: true,
          isRequested: false,
        }))
      }
    }
  }, [selectedOrganization, newDeal.type])

  const handleChange = (event: any) => {
    const { name, value } = event.target
    setNewDeal(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateFields = () => {
    const hasValidVertical =
      selectedOrganization?.vertical && !selectedOrganization.vertical.includes('No Vertical Assigned')

    const errors = {
      organization:
        (newDeal && Array.isArray(newDeal.organizations) && newDeal.organizations.length === 0) || !hasValidVertical,
      type: !newDeal.type,
      startDate: !newDeal.startDate,
      endDate: !newDeal.endDate,
      agreementOption: !agreementOption, // Add this validation
      invoiceOption: !invoiceOption, // Add this validation
      selectedDeal: !selectedDeal,
    }

    setValidationErrors(errors)

    // Ensure all fields are valid, including the vertical
    return !Object.values(errors).some(Boolean)
  }

  useEffect(() => {
    setIsValid(validateFields())
  }, [newDeal])

  // Handle vertical change in CloneDeal
  const handleVerticalChange = (event: any) => {
    const newVerticals = event.target.value // Get the new vertical values

    // Update the vertical in state without triggering backend update yet
    setSelectedOrganization(prev => ({
      ...prev,
      vertical: newVerticals, // Update the selected organization's vertical
    }))

    setNewDeal(prev => ({
      ...prev,
      organizations: [prev.organizations[0]], // Keep other organization-related fields intact
    }))
  }

  // Handle the blur event to trigger backend update
  const handleVerticalBlur = (event: any) => {
    const { name, value } = event.target

    console.log('Vertical Blur Event:', { name, value }) // Debugging the blur event

    // Ensure that a value exists before proceeding
    if (value) {
      // Update the state (trigger the vertical update)
      handleChange({ target: { name, value } })
      handleVerticalUpdate(value) // Call the backend update function
    } else {
      // Handle the case when no value is provided (optional)
      console.warn(`No value provided for ${name}. Setting to default.`)
      handleChange({ target: { name, value: '' } })
      handleVerticalUpdate('') // Send empty value to backend
    }
  }

  // Call this function on blur to trigger the backend update
  const handleVerticalUpdate = (updatedVertical: any) => {
    if (!selectedOrganization || !selectedOrganization.org_uuid) {
      console.error('No valid organization selected for vertical update')
      return
    }

    const updates = [
      {
        primaryKey: 'org_uuid',
        tableName: 'organizations',
        itemId: selectedOrganization.org_uuid, // Use the organization's unique ID
        fieldName: 'vertical',
        newValue: updatedVertical, // Send the correct vertical array
      },
    ]

    // Perform the update
    updateItems(
      { updates },
      {
        onSuccess: data => {
          console.log('Vertical updated successfully: ', data)
          showSnackbar('Organization vertical updated successfully', 'success')
          setOpenTooltip(false)
        },
        onError: error => {
          console.error('Error updating vertical: ', error)
          showSnackbar('Error updating organization vertical', 'error')
        },
      }
    )
  }

  // Ensure this function is not called directly in render logic
  const onChangeOrganization = async (newValue: any) => {
    console.log('newValue', newValue, newDeal?.organizations)
    if (!newValue) {
      console.log('No value detected, clearing....')
      setNewDeal(prev => ({
        ...prev,
        organizations: [],
        name: '',
        dba: '',
        contacts: [],
        startDate: '',
        endDate: '',
      }))
      setUniqueIdentifier('')
      setSelectedContacts([])
      setMissingFields([])
      setIconColor('gray')
      setSelectedOrganization(null)
      setSelectedDeal(null)
      return
    }

    handleSelectOrganization(newValue)
    // setSelectedDeal(null)
    const primaryContactIds =
      newValue && newValue.primaryContact && Array.isArray(newValue.primaryContact) ? newValue.primaryContact : []
    const additionalContactIds =
      newValue && newValue.contacts && Array.isArray(newValue.contacts) ? newValue.contacts : []
    const contactIds = [...additionalContactIds, ...primaryContactIds]

    await fetchContactsForOrganization(contactIds)

    const newIdentifier = getUniqueIdentifier(deals?.map(deal => deal.name))
    setUniqueIdentifier(newIdentifier)
    const newDealName = dealTypeName ? `${newValue.name} - ${dealTypeName}` : `${newValue.name} - ${prefillData?.type}`

    if (originOrganization?.org_uuid !== newValue?.org_uuid) {
      setNewDeal(prev => ({
        ...prev,
        recipients: { contactRecipient: [], authorizedRecipient: [] },
        authorizedDocumentLink: '',
        contactDocumentLink: '',
        stage: 'Proposal',
      }))
    }
    setNewDeal(prev => ({
      ...prev,
      organizations: [newValue.id || newValue.org_uuid],
      name: newDealName, // Combine organization name and deal type
      dba: newValue.dba,
      contacts: contactIds,
      products: [],
      stage: 'Proposal',
      // isCreated: false,
      // isApproved: false,
      // isRequested: false,
    }))

    const newSelectedContacts = (contacts as Contact[]).filter(contact => contactIds.includes(contact.contacts_uuid))
    setSelectedContacts(newSelectedContacts)
  }

  const handleAddContact = (newValue: any) => {
    setNewDeal(prev => ({
      ...prev,
      contacts: Array.isArray(newValue)
        ? newValue.map(item => item.contacts_uuid)
        : newValue
        ? [newValue.contacts_uuid]
        : [],
    }))
  }

  // Ref to store the Dialog element
  const dialogRef = useRef(null)

  const handleDealTypeChange = (newValue: any) => {
    const isCSAType = ['CSA', 'RCSA'].includes(newValue)
    const isTypeIO = newValue === 'IO'
    const isTypeMSA = newValue === 'MSA'

    setDealTypeName(newValue)

    setNewDeal(prev => {
      let needsApproval = prev.needsApproval

      // If it's an MSA deal, check if the vertical is "Student" or "Multifamily"
      if (isTypeMSA) {
        const requiresApproval =
          selectedOrganization &&
          selectedOrganization?.vertical &&
          Array.isArray(selectedOrganization?.vertical) &&
          selectedOrganization?.vertical.some(v => verticalsRequiringApproval.includes(v)) &&
          !['Student', 'Multifamily', 'Student Housing'].includes(selectedOrganization?.vertical[0])

        needsApproval = requiresApproval
      }

      return {
        ...prev,
        name: selectedOrganization ? `${selectedOrganization?.name} - ${newValue}` : '',
        type: newValue,
        needsApproval: isCSAType ? true : isTypeMSA ? needsApproval : prev.needsApproval,
      }
    })
  }

  const handleAgreementOption = (event: any) => {
    const selectedType = event.target.value
    setAgreementOption(selectedType)
    setNewDeal(prev => ({
      ...prev,
      agreementOption: selectedType, // Add to the deal's properties
    }))
  }
  const handleInvoiceOptionChange = (event: any) => {
    const selectedInvoiceOption = event.target.value
    setInvoiceOption(selectedInvoiceOption)
    setNewDeal(prev => ({
      ...prev,
      invoiceOption: selectedInvoiceOption, // Add to the deal's properties
    }))
  }
  const onNoSignatureChange = (name: string, value: any) => {
    handleChange({ target: { name, value } })

    if (name === 'noSignature') {
      const checked = !!value
      setNoSignature(checked)
      setLocalStage(checked ? 'Deal Won' : newDeal?.stage ?? '')
    }
  }
  const addPredecessorSelection = (predecessor: any) => {
    if (!predecessor) {
      // Clear only predecessor-related fields, NOT core deal info
      setNewDeal(prev => ({
        ...prev,
        predecessorId: '',
        isPredecessor: false,
        oldContractId: '',
        products: [],
        startDate: '',
        endDate: '',
      }))
      setSelectedDeal(null)

      return
    }
    setSelectedDeal(predecessor)
    setNewDeal(prev => ({
      ...prev,
      name: `${selectedOrganization?.name} - ${predecessor?.type}`,
      predecessorId: predecessor?.deal_uuid,
      isPredecessor: true,
      oldContractId: predecessor?.contractId ?? '',
      products: predecessor?.products ?? [],
      recipients: predecessor?.recipients ?? {},
      contacts: predecessor?.contacts ?? prev.contacts, // 👈 don't reset if unnecessary
      organizations: predecessor?.organizations ?? prev.organizations, // 👈 same
      owner: predecessor?.owner ?? prev.owner,
      primaryContact: predecessor?.primaryContact ?? prev.primaryContact,
      startDate: predecessor?.startDate ? predecessor?.startDate : '',
      endDate: predecessor?.endDate ? predecessor?.endDate : '',
      type: predecessor?.type ? predecessor?.type : '',
    }))
  }
  const handleClearDeal = () => {
    setSelectedDeal(null)
  }
  const isLoading = isContactsLoading || isActiveUserLoading || fetchedContactsLoading
  return (
    <>
      <Dialog
        open={open}
        onClose={closeCloneDeal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            ...formDialogStyles({ theme, disabled: selectedOrganization?.length < 1, value: selectedOrganization }),
            gap: 2,
          },
        }}
        slotProps={{ backdrop: (() => {}) as any }}
        disableEscapeKeyDown
        ref={dialogRef}
      >
        <CreateDealHeader context={'headerContext'} cloneMode={cloneMode} />
        <BoxScroller
          sx={{
            '&::-webkit-scrollbar-thumb': {
              backgroundColor:
                theme.palette.mode === 'dark' ? theme.palette.background.scrollbar_thumb : alpha(theme.palette.text.primary, 0.5),
              borderRadius: '6px',
            },
          }}
        >
          <DialogContent ref={dialogRef} sx={dialogContentStyles}>
            {isLoading ? (
              <FormLoading />
            ) : (
              <Stack gap={1}>
                <Grid container>
                  <Grid item xs={10}>
                    <CreateDealOrgAutocomplete
                      organizations={organizations}
                      multiple={false}
                      label={'Select Organization'}
                      value={selectedOrganization}
                      addOrganization={onChangeOrganization}
                      setIsValid={setIsValid}
                      error={validationErrors.organization} // Pass error state
                      openTooltip={openTooltip}
                      setOpenTooltip={setOpenTooltip}
                      isDisabled={cloneMode === 'replace'}
                    />
                  </Grid>
                  <ValidateDiscoveryIcons
                    organization={selectedOrganization}
                    missingFields={missingFields}
                    isLoading={isLoading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ValidateDiscovery
                    organization={selectedOrganization}
                    missingFields={missingFields}
                    setMissingFields={setMissingFields}
                  />
                </Grid>
                {/* Conditional Fields */}
                <Grid container spacing={0} alignItems="center">
                  {selectedOrganization &&
                    (!Array.isArray(selectedOrganization?.vertical) ||
                      selectedOrganization?.vertical.length === 0 ||
                      selectedOrganization?.vertical.includes('No Vertical Assigned')) && (
                      <Grid item xs={12} sm={6} xl={12}>
                        <EditVerticalAutocomplete
                          name="vertical"
                          value={selectedOrganization.vertical || []}
                          onChange={handleVerticalChange}
                          onBlur={handleVerticalBlur}
                          hasDeals={selectedOrganization?.deals?.length > 0}
                        />
                      </Grid>
                    )}
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <SelectAgreementOption
                        agreementOption={agreementOption}
                        onChange={handleAgreementOption}
                        validationErrors={validationErrors}
                        isSelected={selectedOrganization?.length > 0}
                        isDisabled={cloneMode ? true : false}
                      />
                    </Grid>
                    {/* Invoice Option */}
                    <Grid item xs={12} sm={6}>
                      <SelectInvoiceOption
                        invoiceOption={invoiceOption}
                        onChange={handleInvoiceOptionChange}
                        validationErrors={validationErrors}
                        isSelected={selectedOrganization?.length > 0}
                        isCloning={agreementOption && agreementOption === 'Replace Existing Business' ? true : false}
                        isDisabled={cloneMode ? true : false}
                      />
                    </Grid>
                    {agreementOption &&
                      (agreementOption === 'Replace Existing Business' ||
                        agreementOption === 'New/Add-on Business') && (
                        <Grid item xs={12} sm={12}>
                          <DealsAutocomplete
                            deals={organizationDeals ?? []}
                            label={cloneMode === 'clone' ? 'Select deal to be cloned' : 'Select deal to be replaced'}
                            selectedDeal={selectedDeal}
                            setSelectedDeal={setSelectedDeal}
                            onAddDeal={addPredecessorSelection}
                            onClearDeal={handleClearDeal}
                            isDisabled={!selectedOrganization || cloneMode === 'replace' ? true : false}
                            error={validationErrors?.selectedDeal}
                          />
                        </Grid>
                      )}
                  </Grid>
                </Grid>
                <Grid container alignItems="center">
                  <Grid item xs={8}>
                    {/* <StageDefaultTextInput
                      stage={newDeal.stage}
                      handleChange={handleChange}
                      error={selectedOrganization?.length < 1 ? false : validationErrors.type}
                      helperText={validationErrors.type && 'Deal type is required'}
                      noSignature={noSignature}
                    /> */}
                    <StageDefaultTextInput stage={localStage} handleChange={handleChange} />
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ExistingDeal
                      noSignature={!!newDeal?.noSignature}
                      newDeal={newDeal}
                      organization={selectedOrganization}
                      handleChange={onNoSignatureChange}
                      context={'createDeal'}
                    />
                  </Grid>
                </Grid>
                <Grid container alignItems="center">
                  <Grid item xs={12} xl={12}>
                    <DealTypeSelect
                      newDeal={newDeal}
                      setNewDeal={setNewDeal}
                      error={selectedOrganization?.length < 1 ? false : validationErrors.type}
                      helperText={validationErrors.type && 'Deal type is required'}
                      onChange={handleDealTypeChange}
                    />
                  </Grid>
                </Grid>
                <ContactsAutocomplete
                  contacts={allContacts}
                  selectedContacts={selectedContacts}
                  setSelectedContacts={setSelectedContacts}
                  label="Select Contacts"
                  onChange={handleAddContact}
                  validationErrors={validationErrors}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DateRangePicker
                      localeText={{ start: 'Expected Start Date', end: 'Expected Close Date' }}
                      value={[
                        newDeal.startDate ? dayjs(newDeal.startDate) : null,
                        newDeal.endDate ? dayjs(newDeal.endDate) : null,
                      ]}
                      onChange={newValue => {
                        const [startDate, endDate] = newValue

                        setNewDeal(prev => ({
                          ...prev,
                          startDate: normalizeDate(startDate, 'America/New_York'),
                          endDate: normalizeDate(endDate, 'America/New_York'),
                        }))
                      }}
                      slotProps={{
                        textField: {
                          error:
                            selectedOrganization?.length < 1
                              ? false
                              : validationErrors.startDate || validationErrors.endDate,
                        },
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiMultiInputDateRangeField-separator': {
                          m: 0,
                        },
                        '& .MuiFormControl-root': {
                          m: 0,
                        },
                        '& .MuiInputBase-root': {
                          maxHeight: '56px',
                          m: 0,
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
                {activeUser && activeUser.user && testUsers.includes(activeUser.user.id) ? (
                  <Grid container alignItems="center">
                    <Grid item xs={12}>
                      <CreateProjectTextField
                        label="Probability (%)"
                        name="probability"
                        value={newDeal.probability}
                        onChange={handleChange}
                        fullWidth
                        autoComplete="off"
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 1, // Controls the increment/decrement step
                        }}
                      />
                    </Grid>
                    {/* <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <TestDealCheck isTest={isTest} setIsTest={setIsTest} setNewDeal={setNewDeal} />
                  </Grid> */}
                  </Grid>
                ) : (
                  <CreateProjectTextField
                    label="Probability (%)"
                    name="probability"
                    value={newDeal.probability}
                    onChange={handleChange}
                    fullWidth
                    autoComplete="off"
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 1, // Controls the increment/decrement step
                    }}
                  />
                )}
              </Stack>
            )}
          </DialogContent>
        </BoxScroller>
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              sx={{
                marginLeft: 4,
                backgroundColor: 'rgb(244, 67, 54, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgb(244, 67, 54, 0.7)',
                },
              }}
              onClick={() => closeCloneDeal(null, null)}
              color="primary"
              variant="outlined"
            >
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={event =>
                  handleSaveDealAndNavigate({
                    event,
                    newDeal,
                    assignedProducts: relatedProducts,
                    setNewDeal,
                    setIsCreating,
                    resetDealState,
                    onClose,
                    setIsError,
                    validateFields,
                    cloneMode,
                  })
                }
                disabled={isCreating || !isValid}
                color="secondary"
                variant="contained"
              >
                Next
              </Button>
            </Box>
          </Box>
        </DialogActions>
        {selectedDeal && (
          <CreateDealDisclaimer
            newDeal={(newDeal as unknown) as Deal}
            selectedDeal={(selectedDeal as unknown) as Deal}
            relatedProducts={relatedProducts}
          />
        )}
      </Dialog>
    </>
  )
}

export default CloneDeal
